import type { Inertia, BodyUUID, Vector3, Quaternion } from "../../types/scene";

import { create } from "mutative";
import { generateUuid } from "../../utils/id";
import { logger } from "../../utils/logger";
import { withHistory } from "./withHistory";

import type { SceneStoreSetState, SceneStoreGetState } from "../sceneStore.types";
import type { InertiaActions } from "./actionTypes";

export const ensureInertiaUuid = (inertia: Partial<Inertia> | Inertia): Inertia => {
  if (inertia.uuid) return inertia as Inertia;
  return { ...inertia, uuid: generateUuid() } as Inertia;
};

// MuJoCo rejects non-positive mass and non-positive diagonal-inertia components on
// bodies that declare an explicit <inertial>. Clamp both to a tiny positive minimum so
// edits from the UI can never produce invalid MJCF.
const MIN_INERTIA_VALUE = 1e-9;

const sanitizeInertiaMass = (mass: unknown): number => {
  const n = Number(mass);
  if (!Number.isFinite(n) || n <= 0) return MIN_INERTIA_VALUE;
  return n;
};

const sanitizeInertiaDiagonal = (diagonal: unknown): Vector3 => {
  const arr = Array.isArray(diagonal) ? diagonal : [];
  const out: Vector3 = [MIN_INERTIA_VALUE, MIN_INERTIA_VALUE, MIN_INERTIA_VALUE];
  for (let i = 0; i < 3; i++) {
    const n = Number(arr[i]);
    out[i] = Number.isFinite(n) && n > 0 ? n : MIN_INERTIA_VALUE;
  }
  return out;
};

const sanitizeInertiaPosition = (position: unknown): Vector3 => {
  const arr = Array.isArray(position) ? position : [];
  const out: Vector3 = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    const n = Number(arr[i]);
    out[i] = Number.isFinite(n) ? n : 0;
  }
  return out;
};

const sanitizeInertiaOrientation = (orientation: unknown): Quaternion => {
  const arr = Array.isArray(orientation) ? orientation : [];
  const w = Number(arr[0]);
  const x = Number(arr[1]);
  const y = Number(arr[2]);
  const z = Number(arr[3]);
  if (![w, x, y, z].every(Number.isFinite)) return [1, 0, 0, 0];
  const len = Math.hypot(w, x, y, z);
  if (len < 1e-8) return [1, 0, 0, 0];
  return [w / len, x / len, y / len, z / len];
};

// Apply sanitization to whichever fields are present in a partial inertia update.
const sanitizeInertiaUpdates = (updates: Partial<Inertia>): Partial<Inertia> => {
  const next: Partial<Inertia> = { ...updates };
  if ("mass" in updates) next.mass = sanitizeInertiaMass(updates.mass);
  if ("diagonal" in updates) next.diagonal = sanitizeInertiaDiagonal(updates.diagonal);
  if ("position" in updates) next.position = sanitizeInertiaPosition(updates.position);
  if ("orientation" in updates) next.orientation = sanitizeInertiaOrientation(updates.orientation);
  return next;
};

export const createInertiaActions = (
  set: SceneStoreSetState,
  get: SceneStoreGetState
): InertiaActions => ({
  addInertia: withHistory((bodyUuid: BodyUUID, inertiaInput: Partial<Inertia> = {}) => {
    const state = get();
    const body = state.bodies[bodyUuid] || null;
    if (!body) return null;
    if (body.inertia) {
      logger.warn(`[addInertia] Body "${bodyUuid}" already has inertia; refusing to overwrite`);
      return null;
    }

    const inertia = ensureInertiaUuid({
      name: inertiaInput.name || "inertial",
      mass: sanitizeInertiaMass(inertiaInput.mass ?? 1.0),
      position: sanitizeInertiaPosition(inertiaInput.position ?? [0, 0, 0]),
      orientation: sanitizeInertiaOrientation(inertiaInput.orientation ?? [1, 0, 0, 0]),
      diagonal: sanitizeInertiaDiagonal(inertiaInput.diagonal ?? [0.001, 0.001, 0.001]),
      ...(Array.isArray(inertiaInput.fullinertia) ? { fullinertia: inertiaInput.fullinertia } : {}),
      ...(inertiaInput.uuid ? { uuid: inertiaInput.uuid } : {}),
    });

    set((current) =>
      create(current, (draft) => {
        const target = draft.bodies[bodyUuid] || null;
        if (!target || target.inertia) return;
        target.inertia = inertia;
      })
    );

    return inertia.uuid;
  }),

  setInertiaName: withHistory((bodyUuid: BodyUUID, name: string) =>
    set((state) => {
      const body = state.bodies[bodyUuid] || null;
      if (!body || !body.inertia) return state;

      return create(state, (draft) => {
        const target = draft.bodies[bodyUuid] || null;
        if (!target || !target.inertia) return;
        target.inertia = ensureInertiaUuid({
          ...target.inertia,
          name,
        });
      });
    })
  ),

  updateInertia: withHistory((bodyUuid: BodyUUID, updates: Partial<Inertia>) =>
    set((state) => {
      const body = state.bodies[bodyUuid] || null;
      if (!body || !body.inertia) return state;

      const safeUpdates = sanitizeInertiaUpdates(updates);

      return create(state, (draft) => {
        const target = draft.bodies[bodyUuid] || null;
        if (!target || !target.inertia) return;
        target.inertia = ensureInertiaUuid({
          ...target.inertia,
          ...safeUpdates,
        });
        if ("diagonal" in safeUpdates && safeUpdates.diagonal === undefined) {
          delete (target.inertia as Partial<Inertia>).diagonal;
        }
        if ("fullinertia" in safeUpdates && safeUpdates.fullinertia === undefined) {
          delete (target.inertia as Partial<Inertia>).fullinertia;
        }
      });
    })
  ),

  moveInertiaToBody: withHistory((bodyUuid: BodyUUID, targetBodyUuid: BodyUUID) =>
    set((state) =>
      create(state, (draft) => {
        if (!bodyUuid || !targetBodyUuid || bodyUuid === targetBodyUuid) return;
        const sourceBody = draft.bodies[bodyUuid] || null;
        const targetBody = draft.bodies[targetBodyUuid] || null;
        if (!sourceBody || !targetBody || !sourceBody.inertia) return;

        if (targetBody.inertia) {
          logger.warn(
            `[moveInertiaToBody] Target body "${targetBodyUuid}" already has inertia; refusing to overwrite`
          );
          return;
        }

        targetBody.inertia = sourceBody.inertia;
        delete sourceBody.inertia;
      })
    )
  ),

  removeInertia: withHistory((bodyUuid: BodyUUID) =>
    set((state) =>
      create(state, (draft) => {
        const target = draft.bodies[bodyUuid] || null;
        if (!target) return;
        delete target.inertia;
      })
    )
  ),
});
